/**
 * Collects all error messages from an error object that belong to a specific grouped key.
 *
 * The function scans through the `errors` object and returns every message from keys
 * that start with the given `groupKey` prefix. Useful when API returns flattened error
 * paths such as "data.shipment_detail.weight".
 *
 * @param errors - The error dictionary returned from the API, where each key is a field path and each value is an array of messages.
 * @param groupKey - The base path to match (e.g., "data.shipment_detail").
 * @returns An array of all collected error messages under the specified group. Returns an empty array if none are found.
 *
 * @example
 * const errors = {
 *   "data.shipment_detail.weight": ["Weight exceeds allowed limit"],
 *   "data.shipment_detail.parcels": ["Invalid parcel count"],
 *   "data.customer.name": ["Name is required"]
 * }
 *
 * collectGroupErrors(errors, "data.shipment_detail")
 * // → ["Weight exceeds allowed limit", "Invalid parcel count"]
 */

export const collectGroupErrors = (
  errors: Record<string, string[]>,
  groupKey: string
): string[] => {
  if (!errors) return []

  const prefix = `${groupKey}.`

  return Object.entries(errors)
    .filter(([key]) => key.startsWith(prefix))
    .flatMap(([_, messages]) => messages)
}
